import React from "react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "./ui/alert-dialog";

/**
 * ConfirmDialog — branded replacement for `window.confirm`.
 *
 *   const [openConfirm, setOpenConfirm] = useState(null);
 *   …
 *   <ConfirmDialog
 *     open={!!openConfirm}
 *     onOpenChange={(v) => !v && setOpenConfirm(null)}
 *     title="Delete onboarding?"
 *     description="This removes the record + files. Cannot be undone."
 *     confirmLabel="Delete"
 *     destructive
 *     onConfirm={() => doDelete(openConfirm)}
 *   />
 *
 * Props:
 *   open, onOpenChange  — controlled state from the parent
 *   title, description  — strings or ReactNode
 *   confirmLabel        — defaults to "Confirm"
 *   cancelLabel         — defaults to "Cancel"
 *   destructive         — when true, confirm button is red (delete actions)
 *   onConfirm           — async or sync; dialog closes after the promise settles
 */
export default function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  destructive = false,
  onConfirm,
  testId,
}) {
  const handleConfirm = async (e) => {
    e?.preventDefault?.();
    try { await onConfirm?.(); } finally { onOpenChange?.(false); }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="bg-white border-slate-200" data-testid={testId || "confirm-dialog"}>
        <AlertDialogHeader>
          <AlertDialogTitle className="text-brand-primary">{title}</AlertDialogTitle>
          {description && <AlertDialogDescription className="text-slate-600 whitespace-pre-line">{description}</AlertDialogDescription>}
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="confirm-cancel-btn">{cancelLabel}</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirm}
            data-testid="confirm-action-btn"
            className={destructive
              ? "bg-rose-600 hover:bg-rose-700 text-white font-semibold"
              : "font-semibold"
            }
            style={destructive ? undefined : { background: "#00C7FF", color: "#0F1644" }}
          >
            {confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
